// WP-A · Firebase init: app + Anonymous Auth + App Check + Firestore + Storage.
// Guests never log in (D4); anonymous auth gives a CSRF-safe session that gates
// Firestore/Storage writes via the security rules. App Check is initialized so
// requests carry tokens; enforcement is toggled in the Firebase console (see
// HUMAN_INTERVENTION Q-A1 — reCAPTCHA Enterprise keys must be provisioned for prod).
import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  signInAnonymously,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  CustomProvider,
} from "firebase/app-check";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { firebaseConfig } from "./config.js";

const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// App Check: in production use reCAPTCHA Enterprise with a provisioned site key.
// In dev use a debug provider whose token must be registered in the Firebase
// console (App Check → Apps → "Manage debug tokens"). Without a registered debug
// token, dev writes to enforced services are denied — that is the safe failure
// mode. reCAPTCHA v3 is deprecated; Enterprise is the GCP-native successor
// (HUMAN_INTERVENTION Q-A1). VITE_RECAPTCHA_ENTERPRISE_KEY is read at build time
// (set in .env.local for prod).
//
// For E2E tests, Playwright can set FIREBASE_APPCHECK_DEBUG_TOKEN before this
// module loads. The Firebase SDK exchanges that locally supplied debug token;
// no registered debug token is embedded in the guest-facing bundle.
const recaptchaEnterpriseKey = import.meta.env.VITE_RECAPTCHA_ENTERPRISE_KEY;
const isProd = import.meta.env.PROD;

let appCheck = null;
if (recaptchaEnterpriseKey) {
  appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(recaptchaEnterpriseKey),
    isTokenAutoRefreshEnabled: true,
  });
} else if (isProd) {
  // Prod without a site key: we MUST NOT silently bypass App Check. Log loudly;
  // the security-rule/console-enforcement gate keeps prod protected regardless.
  console.error(
    "[WP-A] VITE_RECAPTCHA_ENTERPRISE_KEY missing in production build — App Check not initialized. " +
      "Provision a reCAPTCHA Enterprise key and rebuild before go-live (HUMAN_INTERVENTION Q-A1)."
  );
} else {
  // Dev debug provider. The token below is a placeholder; the Firebase SDK
  // debug flow logs a real debug token to the console on first run which you
  // register in the Firebase console. We use a CustomProvider so dev never
  // depends on a network reCAPTCHA.
  appCheck = initializeAppCheck(app, {
    provider: new CustomProvider({
      getToken: async () => ({
        token: "WP-A-dev-debug-token",
        expireTimeMillis: Date.now() + 60 * 60 * 1000,
      }),
    }),
    isTokenAutoRefreshEnabled: false,
  });
}
export { appCheck };

// Sign in anonymously as soon as the module loads. The security rules require
// request.auth (anon provider); writes block until this resolves.
let anonReady = null;
export function ensureAnon() {
  if (anonReady) return anonReady;
  setPersistence(auth, browserLocalPersistence).catch(() => {
    /* private-mode browsers reject persistence; ephemeral is fine for a guest */
  });
  anonReady = signInAnonymously(auth).then((cred) => cred.user);
  // On failure drop the cached promise so the next call retries fresh instead
  // of reusing a rejected one — a transient hiccup must not dead-end the guest.
  anonReady.catch(() => { anonReady = null; });
  return anonReady;
}

export function onUser(cb) {
  return onAuthStateChanged(auth, cb);
}

export { app };
